import {
  exceptionFileScope,
  HUBBELL_FEEDBACK_DIVISIONS,
  MASS_RENEWAL_MAX_FEEDBACK_FILES,
  MASS_RENEWAL_MAX_EXCEPTION_FILES,
  productHoldDivision,
  productHoldItems,
  type FeedbackSupplierProfile,
  type HubbellFeedbackDivision,
} from '@erp/rpc';

import {
  hasHeaders,
  type SheetPeek,
  type WorkbookPeek,
} from '../hubbell-feedback/classifyFiles';

/**
 * Whether a dropped file fits the category the user put it in, from its sheet
 * names and first rows (`peekWorkbook`), the way the run reads it on the
 * server (docs/pricing/mass-renewal-upload.md): the Results Workbook by its
 * results header, a Feedback File by its "<code> Feedback" sheet, an
 * Exception List by AS400 ITEM and SELL NET on its first sheet. The user
 * picks the category (the upload area, or the file's category menu; a file
 * named FINAL or Results starts under the Results Workbook); the peek only
 * checks it and says what a misplaced file looks like instead.
 */

/** How far down a sheet a header may sit (a marker row is above it); the run's HEADER_SEARCH_ROWS. */
export const RENEWAL_PEEK_ROWS = 5;

/** The three inputs of a run, each its own upload area on the card. */
export const RENEWAL_SLOTS = ['results', 'feedback', 'exceptions'] as const;
export type RenewalSlot = (typeof RENEWAL_SLOTS)[number];

export type RenewalFileCheck =
  | { kind: 'results' }
  | { kind: 'feedback'; division: HubbellFeedbackDivision }
  /**
   * `holdDivision`: a product-only list, for that Division. `scope`: a priced
   * list whose file name names a Division; without one, each row's DIVISION
   * column decides (blank: every Division).
   */
  | {
      kind: 'exceptions';
      holdDivision?: HubbellFeedbackDivision;
      scope?: HubbellFeedbackDivision;
    }
  /** `missing`: the results headers absent from the closest row; null when no row has more than half. */
  | {
      kind: 'not results';
      missing: string[] | null;
      looksLike: RenewalSlot | null;
    }
  | { kind: 'no feedback sheet'; looksLike: RenewalSlot | null }
  | { kind: 'not an exception list'; looksLike: RenewalSlot | null }
  /** Most of the results header is on it: the Results Workbook, wherever it was put. */
  | { kind: 'has the results header'; looksLike: 'results' }
  /** Its name names two Divisions, or the other supplier: the run refuses it. */
  | { kind: 'conflicting file name' };

/** "  wt   FEEDBACK " → "wt feedback": case and spacing do not tell sheets apart. */
function sheetKey(name: string): string {
  return name.trim().split(/\s+/).join(' ').toLowerCase();
}

/**
 * A file the team names as the Results Workbook: "FINAL.xlsx",
 * "HUB - FINAL.xlsx", "Results.xlsx", "ALL - RESULTS - ABB 11-1-2026.xlsx".
 * A name that also says Feedback or Exception is not one.
 */
export function namesResultsWorkbook(fileName: string): boolean {
  const name = fileName.replace(/\.xlsx$/i, '');
  return /\b(final|results?)\b/i.test(name) && !/feedback|exc/i.test(name);
}

/** The category a dropped file starts under: the Results Workbook by its name, else the area it was dropped on. */
export function dropSlot(fileName: string, area: RenewalSlot): RenewalSlot {
  return namesResultsWorkbook(fileName) ? 'results' : area;
}

function headRows(sheet: SheetPeek): string[][] {
  return sheet.rows.slice(0, RENEWAL_PEEK_ROWS);
}

function headerWithin(sheet: SheetPeek, headers: readonly string[]): boolean {
  return headRows(sheet).some((row) => hasHeaders(row, headers));
}

/**
 * The Division a sheet name such as "WT Feedback" names: a known Division
 * code, then "Feedback", case and spacing aside (as the run reads it).
 */
export function feedbackSheetDivision(
  sheetName: string,
): HubbellFeedbackDivision | undefined {
  const key = sheetKey(sheetName);
  return HUBBELL_FEEDBACK_DIVISIONS.find(
    (division) => key === `${division.code.toLowerCase()} feedback`,
  );
}

/**
 * The columns an Exception List narrows its rows by, which a Results
 * Workbook carries too (the run's FILTER_HEADERS in readExceptionList.ts):
 * they tell neither file from the other.
 */
const EXCEPTION_FILTER_HEADERS = [
  'ACCOUNT',
  'GROUP',
  'LOAD NUM',
  'NEW LOAD NUM',
  'QUOTE SPA',
  'DIVISION',
];

/** The results headers no Exception List has: Title, the prior margins and cost columns. */
function resultsOnlyHeaders(supplier: FeedbackSupplierProfile): string[] {
  return supplier.resultsHeader.filter(
    (header) => !hasHeaders(EXCEPTION_FILTER_HEADERS, [header]),
  );
}

/**
 * The results headers missing from the head row that has the most
 * results-only headers; null unless that row has more than half of them, so
 * the file is no Results Workbook at all.
 */
function missingResultsHeaders(
  peek: WorkbookPeek,
  supplier: FeedbackSupplierProfile,
): string[] | null {
  const only = resultsOnlyHeaders(supplier);
  let best: { row: string[]; present: number } | null = null;
  for (const sheet of peek.sheets) {
    for (const row of headRows(sheet)) {
      const present = only.filter((header) => hasHeaders(row, [header])).length;
      if (present > (best?.present ?? 0)) best = { row, present };
    }
  }
  if (!best || best.present * 2 <= only.length) return null;
  const { row } = best;
  return supplier.resultsHeader.filter((header) => !hasHeaders(row, [header]));
}

function fitsResults(
  peek: WorkbookPeek,
  supplier: FeedbackSupplierProfile,
): boolean {
  return peek.sheets.some((sheet) =>
    headerWithin(sheet, supplier.resultsHeader),
  );
}

function feedbackDivision(
  peek: WorkbookPeek,
): HubbellFeedbackDivision | undefined {
  for (const sheet of peek.sheets) {
    const division = feedbackSheetDivision(sheet.name);
    if (division) return division;
  }
  return undefined;
}

/** What the run reads from a file: its first rows, its name, and whose renewal it is. */
interface FileToCheck {
  peek: WorkbookPeek;
  fileName: string;
  supplier: FeedbackSupplierProfile;
}

function exceptionList({
  peek,
  fileName,
  supplier,
}: FileToCheck): Extract<RenewalFileCheck, { kind: 'exceptions' }> | null {
  const first = peek.sheets[0];
  if (!first) return null;
  const priced = ['AS400 ITEM', 'Supplier Item'].some((item) =>
    ['SELL NET', 'Margin'].some((pricing) =>
      headerWithin(first, [item, pricing]),
    ),
  );
  if (priced) {
    const { division } = exceptionFileScope(fileName);
    return division
      ? { kind: 'exceptions', scope: division }
      : { kind: 'exceptions' };
  }
  const holdDivision = productHoldDivision(fileName, supplier.supplier);
  if (holdDivision && productHoldItems(first.rows))
    return { kind: 'exceptions', holdDivision };
  return null;
}

/** The category a file fits, other than `slot`, in the order the run would read it; null when none. */
function looksLike(slot: RenewalSlot, file: FileToCheck): RenewalSlot | null {
  const fits: Record<RenewalSlot, () => boolean> = {
    results: () => fitsResults(file.peek, file.supplier),
    feedback: () => feedbackDivision(file.peek) !== undefined,
    // Only a list the Exception List check would take: never suggest a move it refuses.
    exceptions: () => checkExceptions(file).kind === 'exceptions',
  };
  return RENEWAL_SLOTS.find((other) => other !== slot && fits[other]()) ?? null;
}

function checkResults(file: FileToCheck): RenewalFileCheck {
  if (fitsResults(file.peek, file.supplier)) return { kind: 'results' };
  const missing = missingResultsHeaders(file.peek, file.supplier);
  // Most of the header is there: a Results Workbook with a column off, not another input.
  return {
    kind: 'not results',
    missing,
    looksLike: missing ? null : looksLike('results', file),
  };
}

function checkExceptions(file: FileToCheck): RenewalFileCheck {
  // A Results Workbook carries AS400 Item and SELL NET too: never take it for a price list.
  if (
    fitsResults(file.peek, file.supplier) ||
    missingResultsHeaders(file.peek, file.supplier)
  )
    return { kind: 'has the results header', looksLike: 'results' };
  // The run refuses a name with two Divisions or the other supplier in it.
  const scope = exceptionFileScope(file.fileName);
  if (
    scope.ambiguous ||
    (scope.supplier && scope.supplier !== file.supplier.supplier)
  )
    return { kind: 'conflicting file name' };
  return (
    exceptionList(file) ?? {
      kind: 'not an exception list',
      looksLike: looksLike('exceptions', file),
    }
  );
}

/** Whether a file fits `slot`, and if not, why and what it looks like instead. */
export function checkRenewalFile(
  slot: RenewalSlot,
  peek: WorkbookPeek,
  supplier: FeedbackSupplierProfile,
  fileName = '',
): RenewalFileCheck {
  const file = { peek, supplier, fileName };
  switch (slot) {
    case 'results':
      return checkResults(file);
    case 'feedback': {
      const division = feedbackDivision(peek);
      return division
        ? { kind: 'feedback', division }
        : { kind: 'no feedback sheet', looksLike: looksLike('feedback', file) };
    }
    case 'exceptions':
      return checkExceptions(file);
  }
}

/** A file on the card: its category, what was read of it, and whether it fits. */
export type DroppedRenewalFile = {
  id: string;
  file: File;
  slot: RenewalSlot;
  /** Kept once read, so a change of category re-checks without reading again. */
  peek?: WorkbookPeek;
  check: RenewalFileCheck | { kind: 'reading' } | { kind: 'unreadable' };
};

/** The file once read: checked against its category. */
export function withPeek(
  entry: DroppedRenewalFile,
  peek: WorkbookPeek,
  supplier: FeedbackSupplierProfile,
): DroppedRenewalFile {
  return {
    ...entry,
    peek,
    check: checkRenewalFile(entry.slot, peek, supplier, entry.file.name),
  };
}

/** The file under another category, re-checked from what was read of it. */
export function movedToSlot(
  entry: DroppedRenewalFile,
  slot: RenewalSlot,
  supplier: FeedbackSupplierProfile,
): DroppedRenewalFile {
  const moved = { ...entry, slot };
  return entry.peek ? withPeek(moved, entry.peek, supplier) : moved;
}

/** Why the drop cannot start yet, in the order the card says them. */
export type RenewalDropIssue =
  /** `missing`: for a Results Workbook with a column off, the results headers it lacks. */
  | {
      issue: 'file does not fit its category';
      fileName: string;
      missing?: string[];
    }
  /** Nothing to re-check under another category: only removing it helps. */
  | { issue: 'file could not be opened'; fileName: string }
  | { issue: 'no results workbook' }
  | { issue: 'several results workbooks'; count: number }
  | { issue: 'no feedback file' }
  | { issue: 'too many feedback files' }
  | { issue: 'division has two feedback files'; code: string }
  | { issue: 'several exception lists'; count: number };

export interface RenewalDropState {
  files: DroppedRenewalFile[];
  resultsWorkbook: File | null;
  feedbackFiles: File[];
  exceptionLists: File[];
  /** A file is still being read. */
  reading: boolean;
  /** What keeps Start disabled; empty when the drop may start. */
  issues: RenewalDropIssue[];
}

/** The files that fit `slot`, in drop order. */
function filesOf(entries: DroppedRenewalFile[], slot: RenewalSlot): File[] {
  return entries.flatMap((entry) =>
    entry.slot === slot && entry.check.kind === slot ? [entry.file] : [],
  );
}

function misfits(entries: DroppedRenewalFile[]): RenewalDropIssue[] {
  return entries.flatMap((entry): RenewalDropIssue[] => {
    const { check } = entry;
    if (check.kind === 'reading' || check.kind === entry.slot) return [];
    const fileName = entry.file.name;
    if (check.kind === 'unreadable')
      return [{ issue: 'file could not be opened', fileName }];
    return [
      check.kind === 'not results' && check.missing
        ? {
            issue: 'file does not fit its category',
            fileName,
            missing: check.missing,
          }
        : { issue: 'file does not fit its category', fileName },
    ];
  });
}

/** Division codes two or more Feedback Files name, in drop order. */
function duplicatedDivisions(entries: DroppedRenewalFile[]): string[] {
  const seen = new Set<string>();
  const duplicated = new Set<string>();
  for (const { check } of entries) {
    if (check.kind !== 'feedback') continue;
    const code = check.division.code;
    if (seen.has(code)) duplicated.add(code);
    seen.add(code);
  }
  return [...duplicated];
}

function dropIssues(
  entries: DroppedRenewalFile[],
  counts: { results: number; feedback: number; exceptions: number },
): RenewalDropIssue[] {
  const issues = misfits(entries);
  if (counts.results === 0) issues.push({ issue: 'no results workbook' });
  if (counts.results > 1)
    issues.push({ issue: 'several results workbooks', count: counts.results });
  if (counts.feedback === 0) issues.push({ issue: 'no feedback file' });
  if (counts.feedback > MASS_RENEWAL_MAX_FEEDBACK_FILES)
    issues.push({ issue: 'too many feedback files' });
  for (const code of duplicatedDivisions(entries))
    issues.push({ issue: 'division has two feedback files', code });
  if (counts.exceptions > MASS_RENEWAL_MAX_EXCEPTION_FILES)
    issues.push({ issue: 'several exception lists', count: counts.exceptions });
  return issues;
}

/**
 * What the drop amounts to: exactly one Results Workbook, one to nine
 * Feedback Files (one per Division) and up to twenty Exception Lists go in;
 * a file that does not fit its category holds Start until it is moved or
 * removed, and one that could not be opened until it is removed.
 */
export function renewalDropState(
  entries: DroppedRenewalFile[],
): RenewalDropState {
  const results = filesOf(entries, 'results');
  const feedbackFiles = filesOf(entries, 'feedback');
  const exceptions = filesOf(entries, 'exceptions');
  return {
    files: entries,
    resultsWorkbook: results.length === 1 ? (results[0] ?? null) : null,
    feedbackFiles,
    exceptionLists: exceptions,
    reading: entries.some((entry) => entry.check.kind === 'reading'),
    issues: dropIssues(entries, {
      results: results.length,
      feedback: feedbackFiles.length,
      exceptions: exceptions.length,
    }),
  };
}
